import { Component, Suspense, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { SkeletonPanel } from "@/components/ui/skeleton";

/**
 * One screen's error boundary. It clears when the address changes, the query included: the site page's tabs are
 * ?tab= on one path, and an error on one tab must not stick to the next (Admiral's Handbook crash on 20 Oct stayed on
 * every tab until a reload). A healthy screen is not remounted when only its query changes, so filters and drafts
 * held in a screen's state survive.
 */
class RouteError extends Component<{ children: ReactNode; resetKey: string }, { failed: boolean; key: string }> {
  state = { failed: false, key: this.props.resetKey };
  static getDerivedStateFromError() { return { failed: true }; }
  static getDerivedStateFromProps(props: { resetKey: string }, state: { failed: boolean; key: string }) {
    return props.resetKey !== state.key ? { failed: false, key: props.resetKey } : null;
  }
  render() {
    if (this.state.failed) return <div role="alert" className="p-6 space-y-3">
      <p>This screen could not load. Your other screens are still available.</p>
      <button className="underline" onClick={() => window.location.reload()}>Reload this screen</button>
    </div>;
    return this.props.children;
  }
}

export function RouteContent() {
  const { pathname, search } = useLocation();
  return <RouteError key={pathname} resetKey={pathname + search}><Suspense fallback={
    <div role="status" aria-label="Loading screen"><SkeletonPanel className="h-64" /></div>
  }><Outlet /></Suspense></RouteError>;
}
